import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, ExternalLink, Newspaper, Search, TriangleAlert } from "lucide-react";

import { EmptyPanel, PageHero } from "@/components/interior-page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import {
  NEWS_FALLBACK_THUMBNAIL,
  fetchResourcePage,
  formatPublishedDate,
  newsReadMoreTarget,
  type ResourceContent,
} from "@/lib/resources";

/** Cards per fetch — a multiple of the 3-column grid so full rows load. */
const PAGE_SIZE = 12;

/**
 * Latest news & articles. Content is admin-managed — see
 * frontend/src/pages/AdminResourceContent.tsx.
 *
 * Read More goes to the article's external URL when it has one, otherwise to
 * the internal detail page (newsReadMoreTarget).
 *
 * Unlike Glossary and FAQ this library grows without bound, so it pages and
 * searches on the server instead of fetching everything up front.
 */
export default function NewsPage() {
  const [articles, setArticles] = useState<ResourceContent[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  // Bumped on every new search so a slow response for an older query can't
  // overwrite the results of a newer one.
  const requestId = useRef(0);

  // Debounce typing so each keystroke doesn't hit the API.
  useEffect(() => {
    const t = setTimeout(() => setAppliedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const id = ++requestId.current;
    setFailed(false);
    setArticles(null);
    fetchResourcePage("news", { search: appliedQuery, pageSize: PAGE_SIZE, offset: 0 })
      .then((page) => {
        if (id !== requestId.current) return;
        setArticles(page.items);
        setHasMore(page.hasMore);
      })
      .catch(() => { if (id === requestId.current) setFailed(true); });
  }, [appliedQuery]);

  const loadMore = () => {
    if (!articles || loadingMore) return;
    const id = requestId.current;
    setLoadingMore(true);
    fetchResourcePage("news", { search: appliedQuery, pageSize: PAGE_SIZE, offset: articles.length })
      .then((page) => {
        if (id !== requestId.current) return;
        setArticles((prev) => [...(prev ?? []), ...page.items]);
        setHasMore(page.hasMore);
      })
      .catch(() => { if (id === requestId.current) setHasMore(false); })
      .finally(() => setLoadingMore(false));
  };

  // The search box stays up while a search is in flight or returned nothing —
  // hiding it then would strand the reader with no way to change the query.
  const showSearch = appliedQuery !== "" || query !== "" || (articles?.length ?? 0) > 0;

  return (
    <div className="public-stage pb-8">
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
        {/* Shared Dark · Gold interior hero — same header as AI Deal
            Matching and the rest of the interior pages. */}
        <div className="mb-8">
          <PageHero
            eyebrow="Resource Library"
            icon={Newspaper}
            title="Latest News &"
            titleAccent="Articles"
            description={"Market updates, deal insights, and platform news from the BusinessLocating team."}
          />
        </div>

        {showSearch && (
          <div className="mb-8 w-full sm:max-w-md">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search articles..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
        )}

        {failed ? (
          <EmptyPanel
            icon={TriangleAlert}
            title="Couldn't load articles"
            description="Something went wrong fetching the latest articles. Please refresh the page to try again."
          />
        ) : articles === null ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : articles.length === 0 ? (
          <EmptyPanel
            icon={Newspaper}
            title={appliedQuery ? "No matching articles" : "No articles yet"}
            description={
              appliedQuery
                ? "Nothing here matches that search. Try a shorter phrase."
                : "There's nothing published right now. Check back soon — new market updates and deal insights land regularly."
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((a) => {
              const target = newsReadMoreTarget(a);
              return (
                <article
                  key={a.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card/60 transition hover:border-primary/40 hover:shadow-sm"
                >
                  {/* Fixed 16:9 box so every card has an identical image area
                      whatever the uploaded file's dimensions are. The box scales
                      with the card width; the image fills it via object-cover
                      (centred), cropping the overflow rather than distorting.
                      Always rendered — an article with no thumbnail gets the
                      placeholder so card heights stay uniform across a row. */}
                  <div className="aspect-video w-full overflow-hidden bg-muted">
                    <img
                      src={a.thumbnail_url || NEWS_FALLBACK_THUMBNAIL}
                      alt=""
                      loading="lazy"
                      className="block h-full w-full object-cover object-center"
                      onError={(e) => {
                        // Guard against an endless loop if the placeholder itself 404s.
                        if (e.currentTarget.src.endsWith(NEWS_FALLBACK_THUMBNAIL)) return;
                        e.currentTarget.src = NEWS_FALLBACK_THUMBNAIL;
                      }}
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    {a.category && (
                      <span className="inline-block text-[0.65rem] font-bold uppercase tracking-wide text-primary">
                        {a.category}
                      </span>
                    )}
                    <h3 className="heading-bold mt-1 text-base text-foreground">{a.title}</h3>
                    {a.short_description && (
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">
                        {a.short_description}
                      </p>
                    )}

                    {/* Spacer pins the footer to the card bottom so all footers align */}
                    <div className="flex-1" />
                    <div className="mt-4 flex items-center justify-between gap-2 border-t border-border/50 pt-3.5">
                      <span className="inline-flex items-center gap-1.5 text-[0.7rem] font-medium text-muted-foreground">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {formatPublishedDate(a.published_at)}
                      </span>

                      {/* Read More resolves in one place (newsReadMoreTarget):
                          an external link when configured, otherwise the
                          internal detail page. Content is never expanded into
                          the card, so every card keeps the same height. */}
                      {target.kind === "external" ? (
                        <a
                          href={target.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary"
                        >
                          Read More
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <Link
                          to={target.to}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary"
                        >
                          Read More
                          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {articles && articles.length > 0 && hasMore && (
          <div className="mt-8 flex justify-center">
            <Button variant="outline" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Loading…" : "Load more articles"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
